import React, { useState, useEffect, useMemo } from 'react';
import { BookOpen, ChevronDown, Tag, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compact picker showing all knowledge items with checkboxes, used to
 * inject extra context into AI generations (briefing / offer).
 *
 * Props:
 *  - password: admin password
 *  - selectedIds: Set<string>
 *  - onChange: (Set<string>) => void
 *  - testIdPrefix: string (e.g. "briefing" or "offer") for unique data-testids
 *  - refreshKey: optional value — when it changes, the list is reloaded
 */
const KnowledgePicker = ({ password, selectedIds, onChange, testIdPrefix = 'know', refreshKey = 0 }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const headers = useMemo(() => ({ 'x-admin-password': password }), [password]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/admin/knowledge/items`, { headers });
        const data = await res.json();
        if (!cancelled) setItems(data.items || []);
      } catch (err) {
        console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const toggle = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(next);
  };

  const selectedCount = selectedIds.size;
  const totalCount = items.length;

  if (totalCount === 0 && !loading) return null;

  return (
    <div className="relative" data-testid={`${testIdPrefix}-knowledge-picker`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md border transition-colors ${
          selectedCount > 0
            ? 'border-orange-300 bg-orange-50 text-orange-800'
            : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400'
        }`}
        data-testid={`${testIdPrefix}-knowledge-toggle`}
      >
        <BookOpen size={12} />
        Wissens-Vorlagen
        {selectedCount > 0 ? (
          <span className="font-bold">· {selectedCount}</span>
        ) : (
          <span className="text-slate-400 font-normal">({totalCount})</span>
        )}
        <ChevronDown size={11} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
          />
          <div
            className="absolute left-0 top-full mt-1 z-50 w-80 max-h-72 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg p-2"
            data-testid={`${testIdPrefix}-knowledge-list`}
          >
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 px-2 py-1 border-b border-slate-100 mb-1">
              Vorlagen mit anhaken — Claude bezieht sie als Kontext ein
            </div>
            {items.map((it) => {
              const checked = selectedIds.has(it.id);
              return (
                <label
                  key={it.id}
                  className={`flex items-start gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors ${
                    checked ? 'bg-orange-50' : 'hover:bg-slate-50'
                  }`}
                  data-testid={`${testIdPrefix}-knowledge-item-${it.id}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(it.id)}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-slate-800 truncate">
                      {it.title}
                    </div>
                    {(it.tags || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {(it.tags || []).slice(0, 4).map((t) => (
                          <span
                            key={t}
                            className="inline-flex items-center gap-0.5 text-[9px] bg-slate-100 text-slate-600 px-1 py-0.5 rounded"
                          >
                            <Tag size={7} />
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  {checked && <Check size={12} className="text-orange-600 mt-0.5" />}
                </label>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default KnowledgePicker;
